import React from 'react'
import ReactMarkdown from 'react-markdown'
import moment from 'moment'
import { makeStyles, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { announcementLinkProps } from './announcementLinks'

const useStyles = makeStyles((theme: Theme) => ({
  image: {
    display: 'block',
    width: '100%',
    maxHeight: 240,
    objectFit: 'cover',
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2)
  },
  date: {
    color: theme.palette.text.secondary,
    display: 'block'
  },
  title: {
    fontWeight: 600,
    marginBottom: theme.spacing(1)
  },
  body: {
    ...theme.typography.body2,
    wordBreak: 'break-word',
    '& p': { margin: theme.spacing(0, 0, 1) },
    '& p:last-child': { marginBottom: 0 },
    '& img': { maxWidth: '100%' },
    '& ul, & ol': { paddingLeft: theme.spacing(3), margin: theme.spacing(0, 0, 1) }
  }
}))

/** Body links: http(s) only, in a new tab with no opener or referrer; anything else is plain text. */
function AnnouncementLink({ href, children }: { href?: string; children?: React.ReactNode }) {
  const props = announcementLinkProps(href)
  return props ? <a {...props}>{children}</a> : <>{children}</>
}

const RENDERERS = { link: AnnouncementLink, linkReference: AnnouncementLink }

export interface AnnouncementContentProps {
  title: string
  /** Markdown. Raw HTML is escaped, never rendered. */
  body: string
  image?: string | null
  /** ISO date shown as the date stamp. */
  publishedAt?: string | null
  /** `id` on the title, for the container's `aria-labelledby`. */
  titleId?: string
  /** Smaller title, for the popup layout. */
  dense?: boolean
}

/**
 * One announcement's content: image, date stamp, title and Markdown body (#25305). The app's modal,
 * popup and history (#25346) and the admin preview (#25350) all render through this, so authors see
 * exactly what users get.
 */
export default function AnnouncementContent({
  title,
  body,
  image,
  publishedAt,
  titleId,
  dense
}: AnnouncementContentProps) {
  const classes = useStyles()
  const date = publishedAt ? moment(publishedAt) : null

  return (
    <div>
      {image && <img className={classes.image} src={image} alt="" />}
      {date?.isValid() && (
        <Typography variant="caption" className={classes.date}>
          {date.format('MMM D, YYYY')}
        </Typography>
      )}
      <Typography variant={dense ? 'subtitle1' : 'h6'} component="h2" id={titleId} className={classes.title}>
        {title}
      </Typography>
      <div className={classes.body}>
        {/* escapeHtml defaults to true in react-markdown 4; keep it (resource-center.md §2). */}
        <ReactMarkdown source={body} escapeHtml renderers={RENDERERS} />
      </div>
    </div>
  )
}
